// src/Components/Streak/RollingNumber.tsx
// A number that rolls from `from` to `to`: the old value slides up and out,
// the new one rises in, after `delay` seconds. Static with reduced motion.
import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

const Presence = AnimatePresence as unknown as React.FC<{ children?: React.ReactNode; initial?: boolean; mode?: "sync" | "wait" | "popLayout" }>;

interface Props {
  from: number;
  to: number;
  delay?: number;
  className?: string;
}

const RollingNumber: React.FC<Props> = ({ from, to, delay = 0.6, className = "" }) => {
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(reduce ? to : from);

  useEffect(() => {
    if (reduce || from === to) {
      setShown(to);
      return undefined;
    }
    setShown(from);
    const t = window.setTimeout(() => setShown(to), delay * 1000);
    return () => window.clearTimeout(t);
  }, [from, to, delay, reduce]);

  return (
    <span className={`streak-roll ${className}`}>
      <span className="visually-hidden">{to}</span>
      <Presence initial={false} mode="popLayout">
        <motion.span
          key={shown}
          className="streak-roll__value"
          aria-hidden="true"
          initial={{ y: "70%", opacity: 0, scale: 0.8 }}
          animate={{ y: "0%", opacity: 1, scale: 1 }}
          exit={{ y: "-70%", opacity: 0, scale: 0.8 }}
          transition={{ type: "spring", stiffness: 420, damping: 26 }}
        >
          {shown}
        </motion.span>
      </Presence>
    </span>
  );
};

export default RollingNumber;
